import React, { useState } from 'react';

import Alert from '@mui/material/Alert';
import { widgetsConfigurations } from 'config/widgetConfigurations/widgetsConfigurations';
import { WidgetView, WidgetViewModeUtils } from 'utilitys/reporting/widgetViewModeUtils';

import { useBaseWidget } from 'hooks/useBaseWidget';

import { LoadingSteps } from 'components/common/LoadingSteps/LoadingSteps';
import { WidgetExistInBackend } from 'components/common/WidgetExistInBackend/WidgetExistInBackend';
import { WidgetTitle } from 'components/common/WidgetTitle/WidgetTitle';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';
import widgetWithTitleClasses from 'components/common/WidgetWithTitle/WidgetWithTitle.module.scss';

import { IComponentProps } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { ThreeDotsMenu } from 'components/Dashboards/Reporting/threeDotsMenu/ThreeDotsMenu';
import { useAutomatedNarrativeLogic } from 'components/widgets/automatedNarrative/AutomatedNarrative.logic';
import { useAutomatedNarrativeProvider } from 'components/widgets/automatedNarrative/AutomatedNarrative.provider';
import { NARRATIVE_PHASES } from 'components/common/LoadingSteps/phases/narrativePhases';
import { NarrativeOf } from 'components/widgets/automatedNarrative/components/narrativeOf/NarrativeOf';

import classes from './AutomatedNarrative.module.scss';

const EDIT_MODE_MESSAGE = 'Edit disabled. Actual Narrative is populated in real time in presentation mode.';
const { id, componentKey, title: defaultTitle } = widgetsConfigurations.automatedNarrative;

export const AutomatedNarrative = ({
    widgetId,
    externalComponents = null,
    reportWidgetRef = null,
    widgetViewMode = WidgetView.page,
    localFilters,
    title = defaultTitle,
}: IComponentProps) => {
    const { ui, api } = useAutomatedNarrativeProvider({ localFilters, widgetId });

    const { logic } = useAutomatedNarrativeLogic({ ui, widgetId });

    const [showNarrative, setShowNarrative] = useState(false);

    const { editableTitle, setEditableTitle } = useBaseWidget({
        id,
        widgetId,
        title,
        ui,
        reportWidgetRef,
    });

    return (
        <WidgetExistInBackend componentKey={componentKey}>
            <WidgetWithTitle
                id={id}
                title={
                    <WidgetTitle title={editableTitle} setTitle={setEditableTitle} widgetViewMode={widgetViewMode} />
                }
                className={WidgetViewModeUtils.isReportView(widgetViewMode) ? widgetWithTitleClasses.inReport : ''}
                titleFilters={[<NarrativeOf key={'narrativeOf'} title={ui.state.parentTitle as string | undefined} />]}
                titleComponents={[
                    externalComponents && <div key={'externalComponents'}>{externalComponents}</div>,
                    !WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode) && (
                        <ThreeDotsMenu
                            widgetViewMode={widgetViewMode}
                            widgetId={widgetId}
                            componentKey={componentKey}
                            parentComponentKey={ui.vars.originParentParentComponentKey}
                            afterDelete={api.onToggleOffAutomatedNarrativeForOriginParentWidget}
                        />
                    ),
                ]}
            >
                <div className={classes.automatedNarrative}>
                    {WidgetViewModeUtils.isReportView(widgetViewMode) && (
                        <div className={classes.narrativeContainer}>
                            <LoadingSteps
                                isLoading={logic.isLoadingNarrative}
                                phases={NARRATIVE_PHASES}
                                onComplete={() => setShowNarrative(true)}
                            />
                            {showNarrative && ui.vars.narrative && (
                                <div
                                    className={classes.narrativeValue}
                                    dangerouslySetInnerHTML={{ __html: ui.vars.narrative }}
                                />
                            )}
                        </div>
                    )}
                    {WidgetViewModeUtils.isReportEditWidget(widgetViewMode) && (
                        <div className={classes.editorView}>
                            <div>
                                <Alert severity={'info'} className={classes.helpTextAlertInfo}>
                                    {EDIT_MODE_MESSAGE}
                                </Alert>
                            </div>
                        </div>
                    )}
                </div>
            </WidgetWithTitle>
        </WidgetExistInBackend>
    );
};
